import React, { useEffect, useState } from "react";

import { Loader } from "@transfr-inc/dashboard-components";
import { useContainerQuery } from "@transfr-inc/dashboard-components/container-query";

import { trackClassroomPageViewed } from "@lib/event-trackers";
import { useApiRequest } from "@lib/hooks";
import { useAllowStudentPrereqToggle, useCurrentUser } from "@queries";
import { useContainer } from "@src/contexts/container-context";

import { CourseCredentials } from "./course-credentials";
import { CourseTopSection } from "./course-top-section";
import { CourseUnitAccordion } from "./course-unit-accordion";
import {
  findCurrentObjective,
  mapCompletedAndLockedData,
  mapMasteries,
} from "./course.utils";
import { UnitLessonAccordion as CourseLessonAccordion } from "./unit-lesson-accordion";

import "./course.tab.scss";
import { usePreviewClassroomModeEnabled } from "@hooks/queries";
import { useAppStore } from "@src/stores/zustand/app-store";
import { useQueryState } from "nuqs";
import { previewParser } from "@lib/utils/search-params-parsers";

const query = {
  "small-panel": {
    minWidth: 320,
    maxWidth: 850,
  },
  "medium-panel": {
    minWidth: 700,
    maxWidth: 900,
  },
  "large-panel": {
    minWidth: 900,
  },
};

export function CourseTab({ id, classroom }) {
  const isDemo = classroom?.isDemo ?? false;

  const isPreviewEnabled = usePreviewClassroomModeEnabled();
  const storedPreviewId = useAppStore((state) => state.previewClassroomId);
  const [previewClassroomId] = useQueryState("preview", previewParser);
  const previewMode =
    (!!previewClassroomId || !!storedPreviewId) && isPreviewEnabled;

  const { courseService, insightsService, classroomService } = useContainer();
  const { classroomId, name } = classroom;
  const [courseData, setCourseData] = useState();
  const [prereqEnabled, setPrereqEnabled] = useState(!isDemo && !previewMode);
  const [credentialData, setCredentialData] = useState();

  const [currentObjective, setCurrentObjective] = useState({
    id: null,
    parentId: null,
  });
  const [params, containerRef] = useContainerQuery(query);

  const { response: data, loading: loadingCourseData } = useApiRequest(() =>
    courseService.getCourseDetails(id)
  );

  const { response: instructors, loading: loadingInstructors } = useApiRequest(
    () => classroomService.getClassroomInstructors(classroomId)
  );

  const {
    loading: loadingActivityDependencies,
    response: activityDependencies,
  } = useApiRequest(() => courseService.getActivityDependencies(id));

  const currentUser = useCurrentUser();
  const allowStudentPrereqToggle = useAllowStudentPrereqToggle();

  const { userId } = currentUser || {};

  const { loading: loadingCourseMastery, response: courseMastery } =
    useApiRequest(() => insightsService.getCourseMastery(userId, id));

  const onTogglePrereq = () => {
    setPrereqEnabled((prePreReq) => {
      const newPrereq = !prePreReq;
      return newPrereq;
    });
  };

  useEffect(() => {
    if (
      !loadingCourseMastery &&
      !loadingCourseData &&
      !loadingActivityDependencies &&
      !loadingInstructors
    ) {
      const masteries = mapMasteries(courseMastery);
      if (data && masteries) {
        const updatedCourse = mapCompletedAndLockedData(
          structuredClone(data),
          masteries,
          activityDependencies ?? {},
          id
        );

        const { currentObjectiveId, parentId } =
          findCurrentObjective(updatedCourse);

        setCurrentObjective({ id: currentObjectiveId, parentId });
        setCourseData(updatedCourse);
      }
    }
  }, [
    courseMastery,
    data,
    loadingCourseMastery,
    loadingCourseData,
    loadingActivityDependencies,
    loadingInstructors,
  ]);

  const unitsComponent = () => {
    return (
      <div className="course-units-container">
        {courseData?.units?.map((unit, index) => (
          <CourseUnitAccordion
            key={unit.id}
            unit={unit}
            unitIndex={index + 1}
            responsiveStyles={params}
            isExpanded={currentObjective.parentId === unit.id}
            courseId={id}
            courseName={courseData.title}
            currentObjectiveId={currentObjective.id}
            credentialData={credentialData}
            completionMap={courseData.completionMap}
            classroomId={classroomId}
            courseData={courseData}
            activityDependencies={activityDependencies}
            preqEnabled={prereqEnabled}
          />
        ))}
      </div>
    );
  };

  const lessonsComponent = () => {
    return (
      <div>
        {courseData?.lessons?.map((lesson, index) => (
          <CourseLessonAccordion
            key={lesson.id}
            lesson={lesson}
            lessonIndex={index + 1}
            responsiveStyles={params}
            courseId={id}
            isExpanded={currentObjective.id === lesson.id}
            courseName={courseData.title}
            unitName={courseData.title}
            credentialData={credentialData}
            completionMap={courseData.completionMap}
            classroomId={classroomId}
            courseData={courseData}
            activityDependencies={activityDependencies}
            preqEnabled={prereqEnabled}
          />
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (classroomId && name) {
      trackClassroomPageViewed(classroomId, name);
    }
  }, [classroom]);

  if (loadingCourseData) {
    return <Loader overlay />;
  }

  return (
    <>
      <CourseCredentials
        setCredentialData={setCredentialData}
        courseData={courseData}
        courseId={id}
        classroomId={classroomId}
        user={currentUser}
      />
      <div>
        <CourseTopSection
          classroom={classroom}
          course={courseData}
          instructors={instructors}
          onTogglePrereq={onTogglePrereq}
          prereqEnabled={prereqEnabled}
          allowStudentPrereqToggle={allowStudentPrereqToggle}
          isDemo={isDemo}
          user={currentUser}
        />
      </div>
      <div className="course-content-container" ref={containerRef}>
        {courseData?.units && unitsComponent()}
        {courseData?.lessons && lessonsComponent()}
      </div>
    </>
  );
}
